import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import {
  CreditCard, Key, AlertTriangle, CheckCircle2, Loader2, Save, Eye, EyeOff,
  ExternalLink, TestTube2, ShieldAlert, Sparkles,
} from "lucide-react";
import { toast } from "sonner";

function SecretInput({ label, placeholder, prefix, hasValue, maskedValue, value, onChange, testid }) {
  const [show, setShow] = useState(false);
  const [editing, setEditing] = useState(false);
  return (
    <div>
      <Label className="flex items-center gap-1.5 text-xs">
        <Key className="w-3 h-3" />{label}
        {hasValue && !editing && <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-600"><CheckCircle2 className="w-3 h-3" />Configuré</span>}
      </Label>
      {hasValue && !editing ? (
        <div className="flex items-center gap-2 mt-1">
          <code className="flex-1 h-9 px-3 rounded-md border bg-[hsl(var(--muted))]/40 text-sm flex items-center font-mono">
            {maskedValue || "••••••••"}
          </code>
          <Button type="button" variant="outline" size="sm" onClick={() => setEditing(true)} className="h-9" data-testid={`${testid}-replace`}>
            Remplacer
          </Button>
        </div>
      ) : (
        <div className="flex items-center gap-2 mt-1">
          <Input
            type={show ? "text" : "password"}
            placeholder={placeholder || prefix}
            value={value}
            onChange={e => onChange(e.target.value)}
            className="font-mono text-sm"
            data-testid={testid}
          />
          <Button type="button" variant="ghost" size="sm" onClick={() => setShow(s => !s)} className="h-9 px-2">
            {show ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          </Button>
          {hasValue && (
            <Button type="button" variant="ghost" size="sm" onClick={() => { setEditing(false); onChange(null); }} className="h-9 text-xs">
              Annuler
            </Button>
          )}
        </div>
      )}
      <div className="text-[10px] text-[hsl(var(--muted-foreground))] mt-1">Format attendu : <code>{prefix}...</code></div>
    </div>
  );
}

export default function PaymentsAdmin() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({
    stripe_mode: "test",
    stripe_test_secret_key: null,
    stripe_test_publishable_key: "",
    stripe_live_secret_key: null,
    stripe_live_publishable_key: "",
    stripe_webhook_secret: null,
    stripe_currency: "eur",
    stripe_country: "FR",
  });
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/payments");
    setConfig(data);
    setForm(f => ({
      ...f,
      stripe_mode: data.stripe_mode,
      stripe_test_publishable_key: data.stripe_test_publishable_key || "",
      stripe_live_publishable_key: data.stripe_live_publishable_key || "",
      stripe_currency: data.stripe_currency,
      stripe_country: data.stripe_country,
    }));
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      // n'envoyer QUE les champs modifiés pour les secrets (null = non modifié)
      const payload = { ...form };
      if (payload.stripe_test_secret_key === null) delete payload.stripe_test_secret_key;
      if (payload.stripe_live_secret_key === null) delete payload.stripe_live_secret_key;
      if (payload.stripe_webhook_secret === null) delete payload.stripe_webhook_secret;
      const { data } = await api.put("/admin/payments", payload);
      setConfig(data);
      setForm(f => ({
        ...f,
        stripe_test_secret_key: null,
        stripe_live_secret_key: null,
        stripe_webhook_secret: null,
      }));
      toast.success("Configuration Stripe enregistrée");
    } catch (e) {
      toast.error("Erreur : " + (e?.response?.data?.detail || e.message));
    } finally { setSaving(false); }
  };

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const { data } = await api.post("/admin/payments/test");
      setTestResult(data);
      if (data.ok) toast.success(`Connexion Stripe OK (mode ${data.mode})`);
      else toast.error(data.error || "Échec du test");
    } catch (e) {
      setTestResult({ ok: false, error: e?.response?.data?.detail || e.message });
    } finally { setTesting(false); }
  };

  if (!config) return <div className="p-10 text-sm text-[hsl(var(--muted-foreground))]">Chargement…</div>;

  const isLive = form.stripe_mode === "live";

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="payments-admin">
      <header className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[color:var(--brand-sky)] font-semibold mb-1">
            <CreditCard className="w-3.5 h-3.5" />Configuration
          </div>
          <h1 className="font-display text-3xl font-bold text-[color:var(--brand-navy)]">Paiements (Stripe)</h1>
          <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1 max-w-2xl">
            Configurez vos clés Stripe pour accepter les paiements sur votre site. Chaque site a sa propre configuration — vos clés ne sont jamais partagées.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={runTest} disabled={testing} data-testid="test-stripe-btn">
            {testing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <TestTube2 className="w-4 h-4 mr-1.5" />}Tester
          </Button>
          <Button onClick={save} disabled={saving} className="bg-[color:var(--brand-navy)]" data-testid="save-payments">
            <Save className="w-4 h-4 mr-1.5" />{saving ? "…" : "Enregistrer"}
          </Button>
        </div>
      </header>

      {/* MODE banner — très visible */}
      <Card className={`p-4 border-2 ${isLive ? "border-red-300 bg-red-50" : "border-amber-300 bg-amber-50"}`} data-testid="mode-banner">
        <div className="flex items-start gap-3">
          {isLive ? <ShieldAlert className="w-6 h-6 text-red-600 shrink-0" /> : <TestTube2 className="w-6 h-6 text-amber-600 shrink-0" />}
          <div className="flex-1">
            <div className={`font-bold ${isLive ? "text-red-800" : "text-amber-800"}`}>
              {isLive ? "🔴 MODE LIVE — Paiements réels en production" : "🧪 MODE TEST — Aucune transaction réelle n'est traitée"}
            </div>
            <div className={`text-xs mt-1 ${isLive ? "text-red-700" : "text-amber-700"}`}>
              {isLive
                ? "Les cartes de vos clients sont réellement débitées. Assurez-vous que votre compte Stripe est activé et vos produits testés."
                : "Utilisez les cartes de test Stripe (ex: 4242 4242 4242 4242, date future, CVC 123). Les clients ne seront pas facturés."}
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Label className="text-xs font-bold">Mode :</Label>
            <Select value={form.stripe_mode} onValueChange={v => {
              if (v === "live" && !config.has_live_secret) {
                if (!window.confirm("⚠️ Aucune clé live configurée. Passer en mode live va empêcher les paiements jusqu'à ce que vous renseigniez une clé sk_live_...")) return;
              }
              setForm(f => ({ ...f, stripe_mode: v }));
            }}>
              <SelectTrigger className="w-28" data-testid="stripe-mode-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="test">Test</SelectItem>
                <SelectItem value="live">Live</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      {/* TEST KEYS */}
      <Card className="p-5 space-y-4">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]">
          <TestTube2 className="w-4 h-4 text-amber-600" />Clés Test (bac à sable Stripe)
        </div>
        <div className="text-xs text-[hsl(var(--muted-foreground))]">Pour tester les flux de paiement sans facturer réellement vos clients. À récupérer dans <a className="underline text-[color:var(--brand-sky)]" href="https://dashboard.stripe.com/test/apikeys" target="_blank" rel="noopener noreferrer">Stripe → Test → Développeurs → Clés API</a>.</div>
        <SecretInput
          label="Clé secrète de test"
          prefix="sk_test_"
          hasValue={config.has_test_secret}
          maskedValue={config.stripe_test_secret_key_masked}
          value={form.stripe_test_secret_key || ""}
          onChange={v => setForm(f => ({ ...f, stripe_test_secret_key: v }))}
          testid="stripe-test-secret"
        />
        <div>
          <Label className="text-xs">Clé publiable de test (optionnel)</Label>
          <Input
            type="text"
            placeholder="pk_test_..."
            value={form.stripe_test_publishable_key}
            onChange={e => setForm({ ...form, stripe_test_publishable_key: e.target.value })}
            className="font-mono text-sm mt-1"
            data-testid="stripe-test-publishable"
          />
        </div>
      </Card>

      {/* LIVE KEYS */}
      <Card className="p-5 space-y-4">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]">
          <ShieldAlert className="w-4 h-4 text-red-600" />Clés Live (production)
        </div>
        <div className="text-xs text-[hsl(var(--muted-foreground))]">Pour les paiements réels. À récupérer dans <a className="underline text-[color:var(--brand-sky)]" href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer">Stripe → Live → Développeurs → Clés API</a> (votre compte Stripe doit être activé).</div>
        <SecretInput
          label="Clé secrète live"
          prefix="sk_live_"
          hasValue={config.has_live_secret}
          maskedValue={config.stripe_live_secret_key_masked}
          value={form.stripe_live_secret_key || ""}
          onChange={v => setForm(f => ({ ...f, stripe_live_secret_key: v }))}
          testid="stripe-live-secret"
        />
        <div>
          <Label className="text-xs">Clé publiable live (optionnel)</Label>
          <Input
            type="text"
            placeholder="pk_live_..."
            value={form.stripe_live_publishable_key}
            onChange={e => setForm({ ...form, stripe_live_publishable_key: e.target.value })}
            className="font-mono text-sm mt-1"
            data-testid="stripe-live-publishable"
          />
        </div>
      </Card>

      {/* WEBHOOK */}
      <Card className="p-5 space-y-3">
        <div className="flex items-center gap-2 font-bold text-[color:var(--brand-navy)]">
          <Sparkles className="w-4 h-4 text-violet-600" />Webhook Stripe
        </div>
        <div className="text-xs text-[hsl(var(--muted-foreground))] space-y-1">
          <div>Stripe doit pouvoir notifier votre site quand un paiement est confirmé. Créez un endpoint webhook dans <a className="underline text-[color:var(--brand-sky)]" href="https://dashboard.stripe.com/webhooks" target="_blank" rel="noopener noreferrer">Stripe → Développeurs → Webhooks</a> avec l'URL suivante :</div>
          <code className="block px-3 py-2 bg-[hsl(var(--muted))]/60 rounded text-[11px] font-mono break-all">{window.location.origin}/api/webhook/stripe</code>
          <div>Puis copiez le "Signing secret" (commence par <code>whsec_...</code>) ci-dessous.</div>
        </div>
        <SecretInput
          label="Signing secret du webhook"
          prefix="whsec_"
          hasValue={config.has_webhook_secret}
          maskedValue={config.stripe_webhook_secret_masked}
          value={form.stripe_webhook_secret || ""}
          onChange={v => setForm(f => ({ ...f, stripe_webhook_secret: v }))}
          testid="stripe-webhook-secret"
        />
      </Card>

      {/* Locale */}
      <Card className="p-5 space-y-3">
        <div className="font-bold text-[color:var(--brand-navy)]">Devise & pays</div>
        <div className="grid md:grid-cols-2 gap-3">
          <div>
            <Label>Devise</Label>
            <Select value={form.stripe_currency} onValueChange={v => setForm({ ...form, stripe_currency: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="eur">EUR (Euro)</SelectItem>
                <SelectItem value="usd">USD (Dollar)</SelectItem>
                <SelectItem value="gbp">GBP (Livre)</SelectItem>
                <SelectItem value="chf">CHF (Franc suisse)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Pays (ISO 2 lettres)</Label>
            <Input value={form.stripe_country} onChange={e => setForm({ ...form, stripe_country: e.target.value })} placeholder="FR" maxLength={2} />
          </div>
        </div>
      </Card>

      {/* TEST RESULT */}
      {testResult && (
        <Card className={`p-5 border-2 ${testResult.ok ? "border-emerald-300 bg-emerald-50" : "border-red-300 bg-red-50"}`} data-testid="test-result">
          <div className="flex items-start gap-3">
            {testResult.ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" /> : <AlertTriangle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />}
            <div className="flex-1">
              <div className={`font-bold ${testResult.ok ? "text-emerald-800" : "text-red-800"}`}>
                {testResult.ok ? `Connexion réussie (mode ${testResult.mode})` : "Échec du test"}
              </div>
              {testResult.ok ? (
                <div className="text-sm text-emerald-700 mt-1">
                  Solde disponible : <strong>{testResult.available_balance?.toFixed(2) ?? "0.00"} {(testResult.currency || "eur").toUpperCase()}</strong>
                </div>
              ) : (
                <div className="text-sm text-red-700 mt-1">{testResult.error}</div>
              )}
            </div>
          </div>
        </Card>
      )}

      <div className="text-center pt-2">
        <a href="https://dashboard.stripe.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-xs text-[color:var(--brand-sky)] hover:underline">
          <ExternalLink className="w-3 h-3" />Ouvrir le tableau de bord Stripe
        </a>
      </div>
    </div>
  );
}
